import React, { useEffect, useState, useMemo, useCallback, memo, useRef } from "react";
import {
  Icon,
  Checkbox,
  Modal,
  Input,
  FormGroup,
  Form,
  FormField,
  Radio,
  Button,
} from "semantic-ui-react";

// serializer
import VenipunctureNoteSerializer from "issara-sdk/types/VenipunctureNoteSerializer_apps_LAB";

// Common

// Interface
import { State, RunSequence } from "react-lib/apps/HISV3/LAB/sequence/LabReport";

// Utils
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Config
import { useIntl } from "react-intl";

// Types
type CardVenipunctureNoteProps = {
  setProp: (key: string, value: any) => any;
  onEvent: (e: any) => any;
  // seq
  runSequence: RunSequence;
  // data
  selectedLabOrderWorking: any;
  selectedVenipunctureNote?: VenipunctureNoteSerializer;
  // CommonInterface
  buttonLoadCheck?: any;
  errorMessage?: any;
  successMessage?: any;
  // callback
  onClose?: (changed: boolean) => any;
  languageUX?: string;
};

const CARD_VENIPUNCTURE_NOTE = "CardVenipunctureNote";

const MAX_REMARK_LENGTH = 200;

const CardVenipunctureNote = (props: CardVenipunctureNoteProps) => {
  const intl = useIntl();

  const handleChangeData = (e: any, data: any) => {
    const name = data?.name || e.target.name;
    const value = data?.value ?? e.target.value;

    if (name === "number_of_needle_attempt") {
      // อนุญาตเฉพาะตัวเลข
      if (!/^\d*$/.test(value)) {
        return;
      }
    }

    props.setProp(`selectedVenipunctureNote.${name}`, value);
  };

  const handleVeinCharacteristic = (e: any, data: any) => {
    const currentValues = props.selectedVenipunctureNote?.vein_characteristic || [];

    const newValues = data.checked
      ? [...currentValues, data.value]
      : currentValues.filter((item) => item !== data.value);

    if (data.value === "other" && !data.checked) {
      props.setProp("selectedVenipunctureNote", {
        ...props.selectedVenipunctureNote,
        vein_characteristic: newValues,
        other_characteristic: "",
      });
    } else {
      props.setProp("selectedVenipunctureNote.vein_characteristic", newValues);
    }
  };

  const handleSaveVN = () => {
    props.onEvent({
      message: "LabOrderArrive",
      params: {
        action: "saveVenipunctureNote",
        cardKey: CARD_VENIPUNCTURE_NOTE,
      },
    });
  };

  return (
    <div className="ui form">
      <SnackMessage
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_VENIPUNCTURE_NOTE}`, null);
          props.setProp(`successMessage.${CARD_VENIPUNCTURE_NOTE}`, null);
        }}
        error={props.errorMessage?.[CARD_VENIPUNCTURE_NOTE]}
        success={props.successMessage?.[CARD_VENIPUNCTURE_NOTE]}
        languageUX={props.languageUX}
      />

      <div style={{ paddingBottom: "10px" }}>
        <div
          style={{
            fontSize: "1.2rem",
            fontWeight: "bold",
            padding: "0.35rem 0 0.35rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <div
              style={{
                width: "45px",
                height: "45px",
                background: "#AFD6FF",
                color: "#164A73",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingLeft: "8px",
              }}
            >
              <Icon name="edit"></Icon>
            </div>
            <div>
              <label style={{ color: "#164A73" }}>
                {intl.formatMessage({ id: "VENIPUNCTURE_NOTE_บันทึกการเจาะเลือด" })}
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  color: "rgba(117, 117, 117, 0.5)",
                  paddingTop: "10px",
                }}
              >
                <div>
                  <label>{props.selectedLabOrderWorking?.patient_name || ""}</label>
                </div>
                <div>•</div>
                <div>
                  <label style={{ paddingRight: "8px" }}>LabID</label>
                  <label>{props.selectedLabOrderWorking?.id || ""}</label>
                </div>
                <div>•</div>
                <div>
                  <label>{props.selectedLabOrderWorking?.date || ""}</label>
                </div>
              </div>
            </div>
          </div>
          <div>
            <Icon className="red" link={true} name="close" onClick={props.onClose}></Icon>
          </div>
        </div>
        <hr></hr>
      </div>
      <div>
        <Form>
          <FormGroup>
            <FormField width={4}>
              <div style={{ display: "flex", fontWeight: "bold", marginTop: "15px" }}>
                <label>{intl.formatMessage({ id: "VENIPUNCTURE_NOTE_ระดับความยาก" })}</label>
                <span style={{ color: "red" }}>*</span>
              </div>
            </FormField>

            <FormField width={12}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {difficultyOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() =>
                      handleChangeData(null, {
                        name: "difficulty_level",
                        value: option.value,
                      })
                    }
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      border:
                        props.selectedVenipunctureNote?.difficulty_level === option.value
                          ? "1px solid var(--primary-theme-color)"
                          : "1px solid rgba(117, 117, 117, 0.5)",
                      background:
                        props.selectedVenipunctureNote?.difficulty_level === option.value
                          ? "var(--primary-select-background)"
                          : "#FFFFFF",
                      borderRadius: "6px",
                      padding: "12px 15px",
                      width: "100%",
                      boxSizing: "border-box",
                      cursor: "pointer",
                    }}
                  >
                    {/* Radio */}
                    <div style={{ marginRight: "10px" }}>
                      <Radio
                        name="difficulty_level"
                        value={option.value}
                        checked={props.selectedVenipunctureNote?.difficulty_level === option.value}
                        onChange={handleChangeData}
                      />
                    </div>

                    {/* ข้อความ */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        lineHeight: 1.5,
                      }}
                    >
                      <label style={{ fontWeight: "bold" }}>
                        {intl.formatMessage({ id: `VENIPUNCTURE_NOTE_${option.title}` })}
                      </label>
                      <label>
                        {intl.formatMessage({ id: `VENIPUNCTURE_NOTE_${option.description}` })}
                      </label>
                    </div>

                    {/* Badge */}
                    <div
                      style={{
                        marginLeft: "auto",
                        background: option.background,
                        color: option.color,
                        borderRadius: "4px",
                        padding: "8px 15px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {option.badge}
                    </div>
                  </div>
                ))}
              </div>
            </FormField>
          </FormGroup>
          <FormGroup className="--override-disabled --grey">
            <FormField width={4}>
              <div style={{ display: "flex", fontWeight: "bold", marginTop: "15px" }}>
                <label>
                  {intl.formatMessage({ id: `VENIPUNCTURE_NOTE_ตำแหน่งเส้นเลือด/ลักษณะเฉพาะ` })}
                </label>
              </div>
            </FormField>

            <FormField width={12}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                {veinCharacteristics.map((option) => {
                  const selectedValues = props.selectedVenipunctureNote?.vein_characteristic || [];

                  const isChecked = selectedValues.includes(option.value);

                  // กรณี "อื่นๆ"
                  if (option.value === "other") {
                    return (
                      <React.Fragment key={option.value}>
                        {/* อื่นๆ */}
                        <div
                          onClick={() =>
                            handleVeinCharacteristic(null, {
                              value: "other",
                              checked: !isChecked,
                            })
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            border: isChecked
                              ? "1px solid var(--primary-theme-color)"
                              : "1px solid rgba(117, 117, 117, 0.5)",
                            borderRadius: "6px",
                            padding: "12px 15px",
                            boxSizing: "border-box",
                            cursor: "pointer",
                            background: isChecked ? "var(--primary-select-background)" : "#FFFFFF",
                          }}
                        >
                          <div style={{ marginRight: "10px" }}>
                            <Checkbox
                              name="vein_characteristic"
                              value="other"
                              checked={isChecked}
                              onChange={handleVeinCharacteristic}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          <label style={{ fontWeight: "bold" }}>
                            {intl.formatMessage({ id: `VENIPUNCTURE_NOTE_อื่นๆ` })}
                          </label>
                        </div>

                        {/* ช่องกรอก */}
                        <Input
                          fluid
                          name="other_characteristic"
                          placeholder={intl.formatMessage({
                            id: `VENIPUNCTURE_NOTE_ระบุตำแหน่ง/ลักษณะเฉพาะเพิ่มเติม`,
                          })}
                          value={props.selectedVenipunctureNote?.other_characteristic || ""}
                          disabled={!isChecked}
                          onChange={handleChangeData}
                        />
                      </React.Fragment>
                    );
                  }

                  return (
                    <div
                      key={option.value}
                      onClick={() =>
                        handleVeinCharacteristic(null, {
                          value: option.value,
                          checked: !isChecked,
                        })
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: isChecked
                          ? "1px solid var(--primary-theme-color)"
                          : "1px solid rgba(117, 117, 117, 0.5)",
                        borderRadius: "6px",
                        padding: "12px 15px",
                        boxSizing: "border-box",
                        cursor: "pointer",
                        background: isChecked ? "var(--primary-select-background)" : "#FFFFFF",
                      }}
                    >
                      <div style={{ marginRight: "10px" }}>
                        <Checkbox
                          name="vein_characteristic"
                          value={option.value}
                          checked={isChecked}
                          onChange={handleVeinCharacteristic}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <label style={{ fontWeight: "bold" }}>
                        {intl.formatMessage({ id: `VENIPUNCTURE_NOTE_${option.label}` })}
                      </label>
                    </div>
                  );
                })}
              </div>
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField width={4}>
              <div style={{ display: "flex", fontWeight: "bold", marginTop: "15px" }}>
                <label>
                  {intl.formatMessage({ id: `VENIPUNCTURE_NOTE_จำนวนครั้งที่แทงเข็ม` })}
                </label>
              </div>
            </FormField>

            <FormField width={12}>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                {/* Minus */}
                <div
                  onClick={() => {
                    const currentValue =
                      Number(props.selectedVenipunctureNote?.number_of_needle_attempt) || 0;

                    if (currentValue > 0) {
                      handleChangeData(null, {
                        name: "number_of_needle_attempt",
                        value: currentValue - 1,
                      });
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(117, 117, 117, 0.5)",
                    borderRadius: "6px",
                    padding: "12px 15px",
                    boxSizing: "border-box",
                    cursor: "pointer",
                  }}
                >
                  <Icon name="minus" style={{ paddingLeft: "4px" }} />
                </div>

                {/* Number */}
                <div style={{ width: "60px" }}>
                  <Input
                    fluid
                    name="number_of_needle_attempt"
                    value={props.selectedVenipunctureNote?.number_of_needle_attempt || 0}
                    onChange={handleChangeData}
                    input={{
                      style: {
                        textAlign: "center",
                      },
                    }}
                  />
                </div>

                {/* Plus */}
                <div
                  onClick={() => {
                    const currentValue =
                      Number(props.selectedVenipunctureNote?.number_of_needle_attempt) || 0;

                    handleChangeData(null, {
                      name: "number_of_needle_attempt",
                      value: currentValue + 1,
                    });
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(117, 117, 117, 0.5)",
                    borderRadius: "6px",
                    padding: "12px 15px",
                    boxSizing: "border-box",
                    cursor: "pointer",
                  }}
                >
                  <Icon name="plus" style={{ paddingLeft: "4px" }} />
                </div>

                <span>{intl.formatMessage({ id: `VENIPUNCTURE_NOTE_ครั้ง` })}</span>
              </div>
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField width={4}>
              <div style={{ display: "flex", fontWeight: "bold", marginTop: "15px" }}>
                <label>{intl.formatMessage({ id: `VENIPUNCTURE_NOTE_บันทึกเพิ่มเติม` })}</label>
              </div>
            </FormField>

            <FormField width={12}>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <textarea
                  rows={4}
                  name="additional_note"
                  placeholder={intl.formatMessage({
                    id: `VENIPUNCTURE_NOTE_เช่น คนไข้มีอาการ Vasovagal (หน้ามืดง่าย) แนะนำให้นอนเจาะ`,
                  })}
                  value={props.selectedVenipunctureNote?.additional_note || ""}
                  style={{ width: "100%", resize: "none" }}
                  onChange={handleChangeData}
                />
              </div>
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField width={16}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                }}
              >
                <Button
                  onClick={props.onClose}
                  size="small"
                  style={{
                    background: "#ADADAD",
                    color: "white",
                  }}
                >
                  {intl.formatMessage({ id: `ปิดหน้าต่าง` })}
                </Button>

                <ButtonLoadCheck
                  setProp={props.setProp}
                  onClick={handleSaveVN}
                  paramKey={`${CARD_VENIPUNCTURE_NOTE}_saveVenipunctureNote`}
                  buttonLoadCheck={
                    props.buttonLoadCheck?.[`${CARD_VENIPUNCTURE_NOTE}_saveVenipunctureNote`]
                  }
                  style={{
                    background: "var(--primary-theme-color)",
                    color: "white",
                  }}
                  size="small"
                  title={intl.formatMessage({ id: `common.save` })}
                />
              </div>
            </FormField>
          </FormGroup>
        </Form>
      </div>
    </div>
  );
};

CardVenipunctureNote.displayName = "CardVenipunctureNote";

export default memo(CardVenipunctureNote);

const difficultyOptions = [
  {
    value: "NORMAL",
    title: "ปกติ",
    description: "เส้นชัด เจาะผ่านใน 1 ครั้ง",
    badge: "Normal/Easy",
    background: "#D9F0C8",
    color: "#3E6B2F",
    border: "#B8D5A0",
  },
  {
    value: "MODERATE",
    title: "ปานกลาง",
    description: "เส้นเล็ก/ลึก ต้องคลำหา แต่เจาะ 1 ครั้งผ่าน",
    badge: "Moderate",
    background: "#FFF1C7",
    color: "#8A6500",
    border: "#E5C85C",
  },
  {
    value: "DIFFICULT",
    title: "ยาก",
    description: "เส้นเปราะ/กลิ้ง/จม หรือต้องเจาะมากกว่า 1 ครั้ง",
    badge: "Difficult",
    background: "#FFD6D6",
    color: "#A52A2A",
    border: "#E5A0A0",
  },
];

const veinCharacteristics = [
  {
    label: "ข้อพับแขนซ้าย",
    value: "left_antecubital",
  },
  {
    label: "ข้อพับแขนขวา",
    value: "right_antecubital",
  },
  {
    label: "หลังมือ",
    value: "back_of_hand",
  },
  {
    label: "เส้นกลิ้งง่าย",
    value: "rolling_vein",
  },
  {
    label: "ผนังเส้นเลือดบาง",
    value: "thin_vessel_wall",
  },
  {
    label: "เส้นเลือดจม/คลำยาก",
    value: "deep_hard_to_palpate",
  },
  {
    label: "อื่นๆ",
    value: "other",
  },
];
